import {FC} from 'react';
import {styled} from 'styled-components';
import {Route, Redirect, Switch} from 'wouter';

import {isLatestTermsVersion} from '@shared/models/term';

import {useResponsive} from '@shared_web/breakpoints';

import {Header} from '@mavismart_frontend/components/header';
import {TabTitle} from '@mavismart_frontend/components/tab_title';
import {AccountVerification} from '@mavismart_frontend/pages/account_verification';
import {DesktopAccount} from '@mavismart_frontend/pages/desktop_account';
import {DesktopHome} from '@mavismart_frontend/pages/desktop_home';
import {DesktopSearch} from '@mavismart_frontend/pages/desktop_search';
import {HomeTabs} from '@mavismart_frontend/pages/home_tabs';
import {LoginPage} from '@mavismart_frontend/pages/login_page';
import {MobileAccount} from '@mavismart_frontend/pages/mobile_account';
import {MobileHome} from '@mavismart_frontend/pages/mobile_home';
import {SupportMessagePage} from '@mavismart_frontend/pages/support_message_page';
import {Terms} from '@mavismart_frontend/pages/terms';
import {useAccount} from '@mavismart_frontend/stores/account_store';
import {useLoginInfo} from '@mavismart_frontend/stores/login_info_store';

export const HOME_FULL_REGEX = new RegExp(
  `^(${[
    '/(groups|doors)?',
    //
    '/groups/(?<groupId>[^/]+)(/[^/]+)?',
    '/groups/(?<groupId>[^/]+)/doors',
    '/groups/(?<groupId>[^/]+)/doors/(?<doorId>[^/]+)(/[^/]+)?',
    '/groups/(?<groupId>[^/]+)/doors/(?<doorId>[^/]+)/messages/(?<category>[^/]+)?',
    '/groups/(?<groupId>[^/]+)/doors/(?<doorId>[^/]+)/messages/(?<category>[^/]+)/send',
    //
    '/doors/(?<doorId>[^/]+)(/[^/]+)?',
    '/doors/(?<doorId>[^/]+)/messages/(?<category>[^/]+)?',
    '/doors/(?<doorId>[^/]+)/messages/(?<category>[^/]+)/send',
  ].join('|')})$`
);

export const SEARCH_FULL_REGEX = new RegExp(
  `^(${[
    '/search',
    '/search/(?<doorId>[^/]+)(/[^/]+)?',
    '/search/(?<doorId>[^/]+)/messages/(?<category>[^/]+)?',
    '/search/(?<doorId>[^/]+)/messages/(?<category>[^/]+)/send',
  ].join('|')})$`
);

export const ACCOUNT_FULL_REGEX = new RegExp(
  `^(${[
    '/user/(?<page>account)',
    '/user/(?<page>notifications)',
    '/user/(?<page>notifications)/(?<notificationId>[^/]+)(/[^/]+)?',
    '/user/(?<page>notifications)/(?<notificationId>[^/]+)/messages/(?<category>[^/]+)?',
    '/user/(?<page>notifications)/(?<notificationId>[^/]+)/messages/(?<category>[^/]+)/send',
    '/user/(?<page>settings)',
  ].join('|')})$`
);

export const App: FC = () => {
  const loginInfo = useLoginInfo();
  const account = useAccount();
  const {isMobile} = useResponsive();

  return (
    <Wrapper>
      {account !== undefined ? (
        <>
          {
            // i am connected
            isLatestTermsVersion(account.termsVersion) ? (
              // terms ok
              <>
                <Header />
                <Switch>
                  {/* Groups & Doors */}
                  <Route path={HOME_FULL_REGEX} component={isMobile ? MobileHome : DesktopHome} />
                  {/* Search */}
                  <Route path={SEARCH_FULL_REGEX} component={isMobile ? HomeTabs : DesktopSearch} />
                  {/* Account & Notifications */}
                  <Route
                    path={ACCOUNT_FULL_REGEX}
                    component={isMobile ? MobileAccount : DesktopAccount}
                  />
                  {/* Support */}
                  <Route
                    path="/support/messages/:threadId/:messageId"
                    component={SupportMessagePage}
                  />
                  {/* Other */}
                  <Route>
                    <Redirect to="/" />
                  </Route>
                </Switch>
              </>
            ) : (
              // terms not ok
              <Switch>
                <Route path="/terms" component={Terms} />
                <Route>
                  <Redirect to="/terms" />
                </Route>
              </Switch>
            )
          }
        </>
      ) : // i am not connected
      loginInfo === undefined ? (
        // login request
        <Switch>
          <Route path="/login" component={LoginPage} />
          <Route>
            <Redirect to="/login" />
          </Route>
        </Switch>
      ) : (
        // enter code
        <Switch>
          <Route path="/account-verification" component={AccountVerification} />
          <Route>
            <Redirect to="/account-verification" />
          </Route>
        </Switch>
      )}
      <TabTitle />
    </Wrapper>
  );
};
App.displayName = 'App';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
